import {CdkDragDrop, DragDropModule, moveItemInArray} from '@angular/cdk/drag-drop';
import {AsyncPipe} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import {FormArray} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatTooltipModule} from '@angular/material/tooltip';
import {select, Store} from '@ngrx/store';
import {BehaviorSubject, map, Observable, take} from 'rxjs';
import {Subtask} from 'src/app/core/model/task';
import {Tracker, TrackerMetric} from 'src/app/core/model/tracker';
import {selectTrackers} from 'src/app/core/store/trackers/trackers.selector';
import {SharedCurrentSkedModule} from 'src/app/shared/current-sked/shared-current-sked.module';
import {OphBackButtonComponent} from 'src/app/shared/design/oph-back-button/oph-back-button.component';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphCheckboxComponent} from 'src/app/shared/design/oph-checkbox/oph-checkbox.component';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {TasksService} from 'src/app/shared/services/tasks.service';
import {NewTrackerInDialogComponent} from 'src/app/shared/trackers/new-tracker-in-dialog/new-tracker-in-dialog.component';
import {SubtaskEditComponent} from '../../subtasks/subtask-edit/subtask-edit.component';
import {ProjectTaskDialogPanels} from '../project-task-dialog-constants';
import {ProjectsTaskSubtaskButtonFooterComponent} from '../subtask-button-footer/projects-task-subtask-button-footer.component';
import {ProjectsTaskSubtaskNewMetricComponent} from '../subtask-new-metric/projects-task-subtask-new-metric.component';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'projects-task-subtask-edit',
  standalone: true,
  imports: [
    CommonModule,
    AsyncPipe,
    MatMenuModule,
    MatTooltipModule,
    SharedCurrentSkedModule,
    OphCheckboxComponent,
    MatIconModule,
    OphIconModule,
    SubtaskEditComponent,
    NewTrackerInDialogComponent,
    ProjectsTaskSubtaskButtonFooterComponent,
    OphButtonModule,
    OphBackButtonComponent,
    ProjectsTaskSubtaskNewMetricComponent,
    DragDropModule,
  ],
  templateUrl: './projects-task-subtask-edit.component.html',
  styleUrl: './projects-task-subtask-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsTaskSubtaskEditComponent implements OnChanges, OnInit {
  @Input() subtaskFormArray: FormArray;
  @Input() fromTemplate: boolean;
  @Input() projectId: string;
  @Input() newTask: boolean;
  @Input() subtaskIndex: number;
  @Input() editorContainerMaxHeight: string;
  @Input() hideAddYourFirstSubtask: WritableSignal<boolean>;
  @Input() editingTask: boolean;

  @Output() changePanel = new EventEmitter<ProjectTaskDialogPanels>();
  @Output() imagesToDeleteChange = new EventEmitter<string[]>();
  @Output() newTaskBack = new EventEmitter<void>();
  @Output() finish = new EventEmitter<void>();
  @Output() newSubtask = new EventEmitter<void>();

  view = signal<'subtask' | 'tracker' | 'metric' | 'newTracker' | 'newMetric'>('subtask');

  metricsCheckedMap = signal<{[key: string]: boolean}>({});
  metricsReadOnlyMap = signal<{[key: string]: boolean}>({});
  metricView = signal<boolean>(false);

  initialMetricsCheckedMap = signal<{[key: string]: boolean}>({});
  initialMetricsReadOnlyMap = signal<{[key: string]: boolean}>({});

  allMetricsChecked = computed(() => {
    const metricsMap = this.metricsCheckedMap();
    const values = Object.values(metricsMap);
    return values.length > 0 && values.every(checked => checked);
  });

  trackers$: Observable<Tracker[]> = this.store$.pipe(select(selectTrackers));
  trackersWithMetrics$: Observable<Tracker[]>;

  selectedTracker$ = new BehaviorSubject<Tracker>(null);
  filteredMetrics$ = new BehaviorSubject<TrackerMetric[]>(null);

  storedSubtask: Subtask;
  imagesArray: string[] = [];

  constructor(
    private tasksService: TasksService,
    private store$: Store
  ) {
    // Watch for changes in view signal and update hideAddYourFirstSubtask accordingly
    // This is only used in the new task creation flow
    effect(
      () => {
        const currentView = this.view();
        if (this.hideAddYourFirstSubtask) {
          this.hideAddYourFirstSubtask.set(
            currentView === 'tracker' ||
              currentView === 'metric' ||
              currentView === 'newTracker' ||
              currentView === 'newMetric'
          );
        }
      },
      {allowSignalWrites: true}
    );
  }

  ngOnInit() {
    this.trackersWithMetrics$ = this.trackers$.pipe(
      map(trackers => trackers.filter(tracker => tracker.metrics?.length))
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.subtaskFormArray && this.subtaskFormArray) {
      this.storedSubtask = {...this.subtaskFormArray.at(this.subtaskIndex).value};
      this.imagesArray = this.tasksService.getImgArrayFromHTMLString(
        this.subtaskFormArray.at(this.subtaskIndex).value?.body || ''
      );

      // Initialize metric selection state
      this.initializeMetricSelectionState();
      this.checkSubtask();
    }
  }

  private initializeMetricSelectionState() {
    const subtaskValue = this.subtaskFormArray.at(this.subtaskIndex).value;
    const checkedMap: {[key: string]: boolean} = {};
    const readOnlyMap: {[key: string]: boolean} = {};

    if (subtaskValue?.metricIds) {
      subtaskValue.metricIds.forEach((m: {metricId: string; readOnly: boolean}) => {
        checkedMap[m.metricId] = true;
        readOnlyMap[m.metricId] = m.readOnly || false;
      });
    }

    this.metricsCheckedMap.set(checkedMap);
    this.metricsReadOnlyMap.set(readOnlyMap);

    // Initialize the initial state maps with the same values
    this.initialMetricsCheckedMap.set({...checkedMap});
    this.initialMetricsReadOnlyMap.set({...readOnlyMap});
  }

  checkSubtask() {
    if (this.subtaskFormArray.at(this.subtaskIndex).value.trackerId) {
      this.setSelectedSubtask();
    }
  }

  setSelectedSubtask() {
    this.trackers$.pipe(take(1)).subscribe(trackers => {
      const tracker = trackers.find(t => t._id === this.subtaskFormArray.at(this.subtaskIndex).value.trackerId);
      if (!tracker) {
        return;
      }
      this.selectedTracker$.next(tracker);
      this.setFilteredMetrics(tracker);
    });
  }

  setFilteredMetrics(tracker: Tracker) {
    const filteredMetrics = tracker.metrics?.filter(m => m.updateable) || [];

    // Get the metricIds array from the subtask form to determine ordering
    const subtaskValue = this.subtaskFormArray.at(this.subtaskIndex).value;
    const metricIdsOrder = subtaskValue?.metricIds || [];

    // Create a map of metricId to its position in the metricIds array
    const orderMap = new Map<string, number>();
    metricIdsOrder.forEach((item: {metricId: string; readOnly: boolean}, index: number) => {
      orderMap.set(item.metricId, index);
    });

    // Get the current checked state to identify newly checked metrics
    const checkedMap = this.metricsCheckedMap();

    // Sort the filtered metrics:
    // - Checked metrics (in metricIds OR in checkedMap) come first, in the order they appear in metricIds
    // - Unchecked metrics come last, maintaining their original order
    const orderedMetrics = [...filteredMetrics].sort((a, b) => {
      const aIsChecked = orderMap.has(a._id) || checkedMap[a._id];
      const bIsChecked = orderMap.has(b._id) || checkedMap[b._id];

      // If both are checked or both are unchecked, sort by their position in metricIds
      if (aIsChecked && bIsChecked) {
        const aOrder = orderMap.has(a._id) ? orderMap.get(a._id)! : Number.MAX_SAFE_INTEGER;
        const bOrder = orderMap.has(b._id) ? orderMap.get(b._id)! : Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      }

      // If only one is checked, checked items come first
      if (aIsChecked) return -1;
      if (bIsChecked) return 1;

      // Both unchecked - maintain original order
      return 0;
    });

    this.filteredMetrics$.next(orderedMetrics);
    this.ensureAllMetricsInMaps(orderedMetrics);
  }

  /**
   * Ensures all filtered metrics are in the checked/readOnly maps.
   * Preserves existing state for saved metrics, initializes new ones as unchecked.
   */
  private ensureAllMetricsInMaps(metrics: TrackerMetric[]) {
    const currentCheckedMap = {...this.metricsCheckedMap()};
    const currentReadOnlyMap = {...this.metricsReadOnlyMap()};

    metrics.forEach(metric => {
      if (!(metric._id in currentCheckedMap)) {
        currentCheckedMap[metric._id] = false;
        currentReadOnlyMap[metric._id] = false;
      }
    });

    this.metricsCheckedMap.set(currentCheckedMap);
    this.metricsReadOnlyMap.set(currentReadOnlyMap);
  }

  onTracker(tracker: Tracker) {
    this.subtaskFormArray.at(this.subtaskIndex).patchValue({
      trackerId: tracker?._id,
    });
    if (tracker) {
      this.selectedTracker$.next(tracker);
      this.setFilteredMetrics(tracker);
      // Emit initial state for new tracker to parent
      const initialMetricCheckedMap = {};
      const initialMetricReadOnlyMap = {};
      tracker.metrics
        ?.filter(m => m.updateable)
        ?.forEach(metric => {
          initialMetricCheckedMap[metric._id] = false;
          initialMetricReadOnlyMap[metric._id] = false; // Default to false
        });
      this.onMetricCheckChange({
        metricId: 'INIT_ALL',
        checked: true,
        initialCheckedMap: initialMetricCheckedMap,
        initialReadOnlyMap: initialMetricReadOnlyMap,
      });
      this.metricView.set(true);
      this.view.set('metric');
    } else {
      // Handle X button click - go back to tracker selection
      this.selectedTracker$.next(null);
      this.filteredMetrics$.next([]);
      this.metricView.set(false);
      this.view.set('tracker');
    }
  }

  onSelectAll() {
    const shouldCheckAll = !this.allMetricsChecked();
    const metricsMap = this.metricsCheckedMap();

    // Emit changes for each metric
    Object.keys(metricsMap).forEach(metricId => {
      this.onMetricCheckChange({
        metricId,
        checked: shouldCheckAll,
      });
    });
  }

  onBack() {
    this.handleCancelChanges();
    if (this.newTask) {
      this.newTaskBack.emit();
    }
  }

  private handleCancelChanges() {
    this.removeImages(true);

    if (this.tasksService.getEditingSubtask()) {
      this.subtaskFormArray.at(this.subtaskIndex).setValue(this.storedSubtask);
    } else {
      this.subtaskFormArray.removeAt(this.subtaskIndex);
    }
  }

  onImageAdded(image: string) {
    this.imagesArray.push(image);
  }

  removeImages(cancel: boolean) {
    let subtaskBody: string;
    if (cancel) {
      subtaskBody = this.storedSubtask?.body || '';
    } else {
      subtaskBody = this.subtaskFormArray.at(this.subtaskIndex).value?.body || '';
    }
    const currentImageArray = this.tasksService.getImgArrayFromHTMLString(subtaskBody);
    const imagesToDelete = this.imagesArray.filter(img => !currentImageArray.includes(img));
    this.imagesToDeleteChange.emit(imagesToDelete);
  }

  onCloseNewTrackerInDialog(metricIds: {metricId: string; readOnly: boolean}[], trackerId?: string) {
    if (trackerId && metricIds) {
      this.subtaskFormArray.at(this.subtaskIndex).patchValue({
        trackerId,
        metricIds,
      });

      // Update the selectedMetrics state to reflect the new tracker's metrics
      const checkedMap: {[key: string]: boolean} = {};
      const readOnlyMap: {[key: string]: boolean} = {};

      metricIds.forEach(metric => {
        checkedMap[metric.metricId] = true;
        readOnlyMap[metric.metricId] = metric.readOnly || false;
      });

      this.metricsCheckedMap.set(checkedMap);
      this.metricsReadOnlyMap.set(readOnlyMap);
    }
    this.view.set('subtask');
  }

  onEditingTaskBack() {
    this.handleCancelChanges();
    this.changePanel.emit(this.newTask ? ProjectTaskDialogPanels.NewSubtasks : ProjectTaskDialogPanels.Default);
  }

  onEditingTaskNext() {
    this.changePanel.emit(this.newTask ? ProjectTaskDialogPanels.NewSubtasks : ProjectTaskDialogPanels.Default);
  }

  onMetricCheckChange(event: {metricId: string; checked: boolean; initialCheckedMap?: any; initialReadOnlyMap?: any}) {
    if (event.metricId === 'INIT_ALL' && event.initialCheckedMap && event.initialReadOnlyMap) {
      // Handle tracker initialization - only update current state
      this.metricsCheckedMap.set(event.initialCheckedMap);
      this.metricsReadOnlyMap.set(event.initialReadOnlyMap);
    } else {
      // Handle individual metric check change
      const currentCheckedMap = {...this.metricsCheckedMap()};
      currentCheckedMap[event.metricId] = event.checked;
      this.metricsCheckedMap.set(currentCheckedMap);

      // Update the form with the current state of checked metrics
      this.updateFormMetricIds();
    }
  }

  onMetricReadOnlyChange(event: {metricId: string; readOnly: boolean}) {
    const currentReadOnlyMap = {...this.metricsReadOnlyMap()};
    currentReadOnlyMap[event.metricId] = event.readOnly;
    this.metricsReadOnlyMap.set(currentReadOnlyMap);

    // Update the form with the current state
    this.updateFormMetricIds();
  }

  /**
   * Updates the form's metricIds array based on the current checked and readOnly maps.
   * Maintains the order of metrics as they appear in filteredMetrics$.
   */
  private updateFormMetricIds() {
    const checkedMap = this.metricsCheckedMap();
    const readOnlyMap = this.metricsReadOnlyMap();
    const filteredMetrics = this.filteredMetrics$.getValue() || [];

    // Build the metricIds array with only checked metrics, maintaining their order
    const metricIds = filteredMetrics
      .filter(metric => checkedMap[metric._id])
      .map(metric => ({
        metricId: metric._id,
        readOnly: readOnlyMap[metric._id] || false,
      }));

    // Update the form
    this.subtaskFormArray.at(this.subtaskIndex).patchValue({
      metricIds,
    });
  }

  onConnectTrackerClicked(viewType: 'tracker' | 'metric') {
    const subtaskValue = this.subtaskFormArray.at(this.subtaskIndex).value;

    if (viewType === 'metric' && subtaskValue?.trackerId) {
      // Set selectedTracker and metrics data using the trackerId from the form
      this.trackers$.pipe(take(1)).subscribe(trackers => {
        const tracker = trackers.find(t => t._id === subtaskValue.trackerId);
        if (tracker) {
          this.selectedTracker$.next(tracker);
          this.setFilteredMetrics(tracker);
          this.metricView.set(true);
        }
      });
    }

    // Set the view as the last step
    this.view.set(viewType);
  }

  onNewMetricClicked() {
    this.view.set('newMetric');
  }

  onNewMetricBack() {
    this.view.set('metric');
  }

  onMetricAdded(updatedTracker: Tracker) {
    // Use the updated tracker returned from the API
    this.selectedTracker$.next(updatedTracker);
    this.setFilteredMetrics(updatedTracker);

    // Initialize the new metric in the checked map
    const currentCheckedMap = {...this.metricsCheckedMap()};
    const currentReadOnlyMap = {...this.metricsReadOnlyMap()};

    // Add the new metrics to the maps (they should be auto-selected by default)
    updatedTracker.metrics
      ?.filter(m => m.updateable)
      .forEach(metric => {
        if (!(metric._id in currentCheckedMap)) {
          currentCheckedMap[metric._id] = true;
          currentReadOnlyMap[metric._id] = false;
        }
      });

    this.metricsCheckedMap.set(currentCheckedMap);
    this.metricsReadOnlyMap.set(currentReadOnlyMap);
    this.setFilteredMetrics(updatedTracker);

    // Go back to metric selection view
    this.view.set('metric');
  }

  onMetricsDrop(event: CdkDragDrop<TrackerMetric[]>) {
    const checkedMap = this.metricsCheckedMap();
    const readOnlyMap = this.metricsReadOnlyMap();
    const filteredMetrics = this.filteredMetrics$.getValue() || [];

    // Build the complete list of checked metrics in their current display order
    const checkedMetrics = filteredMetrics
      .filter(metric => checkedMap[metric._id])
      .map(metric => ({
        metricId: metric._id,
        readOnly: readOnlyMap[metric._id] || false,
      }));

    // Reorder the checked metrics array based on the drag and drop
    moveItemInArray(checkedMetrics, event.previousIndex, event.currentIndex);

    // Update the form with the reordered metricIds
    this.subtaskFormArray.at(this.subtaskIndex).patchValue({
      metricIds: checkedMetrics,
    });

    // Update the filtered metrics display to reflect the new order
    const currentTracker = this.selectedTracker$.getValue();
    if (currentTracker) {
      this.setFilteredMetrics(currentTracker);
    }
  }
}
